import React, { useEffect, useState, useMemo } from "react";
import { Save, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SETTINGS_FIELDS, SECTION_META } from "@/pages/admin/settingsFields";
import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";
import { HeroVideoField } from "@/pages/admin/fields/HeroVideoField";
import { MediaTypeSelect, FocalSelect } from "@/pages/admin/fields/AboutHeroSelects";
import { LogoSizeField } from "@/pages/admin/fields/LogoSizeField";
import { StringListEditor } from "@/pages/admin/fields/StringListEditor";
import { StatsEditor } from "@/pages/admin/fields/StatsEditor";

// Slugify a section name to a DOM id/testid usable as an anchor target.
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

export const SiteSettingsPanel = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", form);
      setForm(res.data);
      toast.success("Site settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const [query, setQuery] = useState("");

  if (!form) {
    return <div className="text-[#A3A3A3] text-sm py-10 text-center">Loading settings…</div>;
  }

  // Filter fields by search query. Matches on label, key, hint, and section
  // — Calvin can type the actual button text ("add to cart") or field key
  // and see only matching fields, with their section headers preserved.
  const q = query.trim().toLowerCase();
  const matches = (f) => {
    if (!q) return true;
    return (
      (f.label || "").toLowerCase().includes(q) ||
      (f.key || "").toLowerCase().includes(q) ||
      (f.hint || "").toLowerCase().includes(q) ||
      (f.section || "").toLowerCase().includes(q)
    );
  };
  const visibleFields = SETTINGS_FIELDS.filter(matches);

  // Group VISIBLE fields by section — empty sections drop out entirely when
  // the search filters them away.
  const sections = visibleFields.reduce((acc, f) => {
    const key = f.section || "General";
    (acc[key] = acc[key] || []).push(f);
    return acc;
  }, {});

  return (
    <div data-testid="site-settings-panel" className="ur-card p-6 sm:p-8 space-y-10 max-w-3xl">
      <div>
        <h2 className="font-display text-2xl font-bold">Site Settings</h2>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Global content &amp; images. Changes go live immediately after saving.
        </p>
      </div>

      {/* Search bar — type the actual button text ("add to cart"), a field
          key, or any word from the hint to filter down to just matching
          fields. Type "add to cart" and only the two Add-to-Cart button
          fields render. Empty query = all sections visible. */}
      <div className="relative">
        <Search className="w-4 h-4 text-[#7A7A7A] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          data-testid="settings-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search settings — try "add to cart" or "view all setups"'
          className="w-full bg-[#0F0F0F] border border-[#262626] rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
        />
      </div>

      {/* Jump-to-section nav — one-click travel to any section without
          scrolling through 20+ headings. Only shows when NOT filtering
          (during a search the visible sections are already narrowed). */}
      {!q && (
        <div className="rounded-xl border border-[#262626] bg-[#0B0B0B] p-4" data-testid="settings-jump-nav">
          <div className="text-[10px] uppercase tracking-widest text-[#FF6600] font-bold mb-2">Jump to section</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(sections).map((name) => (
              <a
                key={name}
                href={`#settings-${slug(name)}`}
                data-testid={`settings-jump-${slug(name)}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(`settings-${slug(name)}`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="text-[11px] text-[#D1D1D1] rounded-full border border-[#333] px-3 py-1 hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
              >
                {name}
              </a>
            ))}
          </div>
        </div>
      )}

      {q && visibleFields.length === 0 && (
        <div className="text-center py-10 text-sm text-[#A3A3A3]" data-testid="settings-no-results">
          No settings match <span className="text-white font-semibold">"{query}"</span>. Try a different keyword.
        </div>
      )}

      {Object.entries(sections).map(([sectionName, fields]) => (
        <div
          key={sectionName}
          id={`settings-${slug(sectionName)}`}
          className="space-y-6"
          data-testid={`settings-section-${slug(sectionName)}`}
        >
          <div className="pb-2 border-b border-[#262626]">
            <h3 className="font-display text-lg font-bold text-white">{sectionName}</h3>
            {SECTION_META[sectionName] && (
              <p className="mt-1 text-[11px] italic text-[#FF6600]/80 font-medium">
                {SECTION_META[sectionName]}
              </p>
            )}
          </div>
          {fields.map((f) => (
            <div key={f.key} className="space-y-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-white font-semibold">{f.label}</div>
                {f.hint && <div className="text-xs text-[#A3A3A3] mt-1">{f.hint}</div>}
              </div>
              {f.type === "text" ? (
                <input
                  data-testid={`settings-field-${f.key}`}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
                />
              ) : f.type === "textarea" ? (
                <textarea
                  data-testid={`settings-field-${f.key}`}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  rows={3}
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600] resize-none"
                />
              ) : f.type === "boolean" ? (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid={`settings-field-${f.key}`}
                    checked={!!form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                    className="w-5 h-5 accent-[#FF6600]"
                  />
                  <span className="text-sm text-[#D1D1D1]">{form[f.key] ? "On" : "Off"}</span>
                </label>
              ) : f.type === "stats" ? (
                <StatsEditor
                  value={form[f.key] || []}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                />
              ) : f.type === "string-list" ? (
                <StringListEditor
                  value={form[f.key] || []}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  testId={`settings-field-${f.key}`}
                />
              ) : f.type === "logo-size" ? (
                <LogoSizeField
                  value={Number(form[f.key]) || 44}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  min={f.min ?? 24}
                  max={f.max ?? 80}
                  testId={`settings-field-${f.key}`}
                  logoSrc={form.site_logo || ""}
                />
              ) : f.type === "logo-width" ? (
                <LogoSizeField
                  value={Number(form[f.key]) || f.default || 140}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  min={f.min ?? 60}
                  max={f.max ?? 220}
                  testId={`settings-field-${f.key}`}
                  logoSrc={form.footer_logo || "/brand/ud-monogram.png"}
                  mode="width"
                />
              ) : f.type === "currency-rate" ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#A3A3A3]">1 USD =</span>
                    <input
                      data-testid={`settings-field-${f.key}`}
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form[f.key] ?? 1.79}
                      onChange={(e) => setForm({ ...form, [f.key]: Number(e.target.value) || 1.79 })}
                      className="w-32 bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
                    />
                    <span className="text-sm text-[#A3A3A3]">AWG</span>
                  </div>
                  <div className="text-xs text-[#A3A3A3]">
                    Preview: <span className="text-white font-semibold">AWG 1,000</span> ≈ <span className="text-[#FF6600] font-semibold">${Math.round(1000 / (Number(form[f.key]) || 1.79))} USD</span>
                  </div>
                </div>
              ) : f.type === "hero-video" ? (
                <HeroVideoField form={form} setForm={setForm} />
              ) : f.type === "about-hero-desktop-video" ? (
                <HeroVideoField
                  form={form}
                  setForm={setForm}
                  enabledKey="about_hero_desktop_video_enabled_shim"
                  mp4Key="about_hero_desktop_video_mp4"
                  webmKey="about_hero_desktop_video_webm"
                  posterKey="about_image"
                  posterHint="About desktop image is used as the video poster + fallback"
                  hideEnabledToggle
                />
              ) : f.type === "about-hero-mobile-video" ? (
                <HeroVideoField
                  form={form}
                  setForm={setForm}
                  enabledKey="about_hero_mobile_video_enabled_shim"
                  mp4Key="about_hero_mobile_video_mp4"
                  webmKey="about_hero_mobile_video_webm"
                  posterKey="about_image_mobile"
                  posterHint="About mobile image is used as the video poster + fallback"
                  hideEnabledToggle
                />
              ) : f.type === "media-type-select" ? (
                <MediaTypeSelect
                  value={form[f.key] || "image"}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  testId={`settings-${f.key.replace(/_/g, "-")}`}
                />
              ) : f.type === "focal-select" ? (
                <FocalSelect
                  value={form[f.key] || "center"}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  testId={`settings-${f.key.replace(/_/g, "-")}`}
                />
              ) : f.type === "recipient-preview" ? (
                <RecipientPreview
                  recipients={form.setup_order_notification_emails}
                  replyTo={form.setup_reply_to_email}
                  confirmationEnabled={form.setup_customer_confirmation_enabled}
                />
              ) : (
                <ImageUploadField
                  value={form[f.key] ?? ""}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  testId={`settings-upload-${f.key}`}
                  recommended={f.recommended}
                />
              )}
            </div>
          ))}
        </div>
      ))}

      <button
        data-testid="settings-save"
        onClick={save}
        disabled={saving}
        className="ur-btn-primary w-full disabled:opacity-40"
      >
        {saving ? "Saving…" : "Save Site Settings"} <Save className="w-4 h-4" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// RecipientPreview — read-only summary showing which admin emails currently
// receive each request stream. Deliberately non-editable and never renders
// any secret / credential (API keys, SMTP passwords, Sentoo tokens, etc.).
// The recipient list itself is edited under "Setup Configurator — Order
// Notifications" above; this component just reads the same value so admins
// can see routing at a glance.
// ---------------------------------------------------------------------------
const STREAMS = [
  { key: "setup",    label: "Workstation / Setup Orders",   note: "Every /setups/[slug] submission" },
  { key: "software", label: "Software Requests",            note: "Every /software request" },
  { key: "service",  label: "Service Quote Requests",       note: "Every Request Estimate flow on /services/[slug]" },
  { key: "volume",   label: "Volume Laptop Solutions",      note: "Every /workstations volume inquiry" },
];

const RecipientPreview = ({ recipients, replyTo, confirmationEnabled }) => {
  const parsed = (recipients || "solutions@allurdesigns.com")
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const reply = (replyTo || "").trim() || "(fallback to sender address)";
  const confirmOn = confirmationEnabled !== false;

  return (
    <div data-testid="settings-recipient-preview" className="rounded-xl border border-[#262626] bg-[#0F0F0F] p-4 space-y-4">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">
          Admin Recipients
        </div>
        <ul className="mt-2 space-y-1">
          {parsed.length === 0 ? (
            <li className="text-xs italic text-[#7A7A7A]">
              No recipients configured — falling back to <span className="text-white">solutions@allurdesigns.com</span>.
            </li>
          ) : (
            parsed.map((email) => (
              <li key={email} className="text-sm text-white font-medium">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF6600] align-middle mr-2" />
                {email}
              </li>
            ))
          )}
        </ul>
        <div className="mt-2 text-[11px] text-[#A3A3A3]">
          Reply-To: <span className="text-white">{reply}</span>
        </div>
      </div>

      <div className="border-t border-[#262626] pt-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">
          Streams Handled
        </div>
        <ul className="mt-2 space-y-2">
          {STREAMS.map((s) => (
            <li
              key={s.key}
              data-testid={`recipient-preview-${s.key}`}
              className="grid grid-cols-[auto_1fr] gap-2 items-baseline"
            >
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#FF6600]">
                <span className="inline-block w-1 h-1 rounded-full bg-[#FF6600]" /> {s.label}
              </span>
              <span className="text-xs text-[#A3A3A3]">{s.note}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-[#262626] pt-3 text-[11px] text-[#A3A3A3] leading-relaxed">
        Customer confirmation email is{" "}
        <span className={confirmOn ? "text-emerald-400 font-semibold" : "text-amber-400 font-semibold"}>
          {confirmOn ? "ON" : "OFF"}
        </span>{" "}
        — governs every request stream (setup / software / service / volume). Toggle
        it under <span className="text-white">"Send Customer Confirmation Email"</span> above.
      </div>

      <div className="text-[11px] text-[#7A7A7A] italic">
        This panel is read-only. No API keys, SMTP passwords or provider
        credentials are shown anywhere in the admin UI.
      </div>
    </div>
  );
};
